import {
  FETCH_ESTIMATE,
  FETCH_ESTIMATE_SUCCESS,
  FETCH_ESTIMATE_FAILED,
  CLEAR_ESTIMATE
} from "../store/types";
import { firebase } from "../config/configureFirebase";

/**
 * Fold/normalize text for robust keyword matching:
 * - lowercases
 * - removes accents/diacritics (e.g. "aéroport" -> "aeroport")
 */
function foldText(s = "") {
  return String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function decodePolylinePoints(encoded) {
  if (!encoded || typeof encoded !== "string") return [];
  const poly = [];
  let index = 0, len = encoded.length;
  let lat = 0, lng = 0;
  while (index < len) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
    lng += dlng;
    poly.push({ latitude: lat / 1e5, longitude: lng / 1e5 });
  }
  return poly;
}

export const getEstimate = (bookingData) => async (dispatch) => {
  dispatch({
    type: FETCH_ESTIMATE,
    payload: bookingData,
  });

  // [SECURE] Pricing must be calculated server-side to generate a signed pricingToken
  const projectId = firebase.config?.projectId || "drive2go-c806b";
  const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
  const functionsBase = (
    (typeof process !== "undefined" && process.env?.REACT_APP_FUNCTIONS_URL) ||
    `https://${region}-${projectId}.cloudfunctions.net`
  ).replace(/\/+$/, "");

  // If routeDetails missing, we can't estimate (unless backend recalculates, but we prefer providing it)
  if (!bookingData.routeDetails && !bookingData.pickup) {
    dispatch({ type: FETCH_ESTIMATE_FAILED, payload: "Missing Route/Pickup Data" });
    return;
  }

  // [FIX] tripType fallback (avoid mismatch if UI forgot to set bookingData.tripType)
  const tripTypeNormalized =
    bookingData.tripType ||
    bookingData.instructionData?.tripType ||
    "one_way";

  const isHourly = tripTypeNormalized === "hourly";

  // Normalize Hours: Only if hourly
  let hoursNormalized = null;
  if (isHourly) {
    const h =
      bookingData.bookingOptions?.hours ||
      bookingData.options?.hours ||
      bookingData.hours ||
      null;

    hoursNormalized = (h === null || h === undefined || h === "") ? null : Number(h);
    if (hoursNormalized !== null && !Number.isFinite(hoursNormalized)) {
      hoursNormalized = null;
    }
  }

  // Normalize CarType: DEFAULT TO 'ALL_CARS' if undefined
  const carTypeInput = bookingData.carType || bookingData.carDetails?.name;
  const carTypeNormalized = carTypeInput || "ALL_CARS";

  // [FIX] A1: Unified Options Builder (STRICT Mapping)
  const buildEstimateOptions = (data, tripType) => {
    const opt = data.bookingOptions || data.options || {};
    const wait = Number(opt.waiting_time || opt.waitingMinutes || opt.waitingTime || 0);

    // [HOURLY_GUARD]
    const normalizedWait = (tripType === "hourly") ? 0 : wait;
    //console.log("[HOURLY_GUARD] tripType:", tripType, "sentWaiting:", wait, "normalizedWaiting:", normalizedWait);

    return {
      passengersCount: Number(opt.passengers || opt.passengersCount || 1),
      luggageCount: Number(opt.luggage || opt.luggageCount || 0),
      babySeatCount: Number(opt.baby_seat || opt.babySeatCount || opt.babySeat || 0),
      childSeatCount: Number(opt.child_seat || opt.childSeatCount || opt.childSeat || 0),
      boosterSeatCount: Number(opt.booster_seat || opt.boosterSeatCount || opt.boosterSeat || 0),
      waitingMinutes: normalizedWait
    };
  };

  const isReturnEnabled = !!(
    bookingData.bookingOptions?.returnTrip ||
    bookingData.options?.returnTrip ||
    (bookingData.returnTrip && bookingData.returnTrip.enabled)
  );

  const optionsNormalized = buildEstimateOptions(bookingData, tripTypeNormalized);

  // --- Helpers: coords/meta normalization (STRICT) ---
  const toLatLng = (input) => {
    if (!input || typeof input !== "object") return null;

    // Accept shapes:
    // - {lat,lng}
    // - {latitude,longitude}
    // - {coords:{lat,lng}} or {coords:{latitude,longitude}}
    const src = (input.coords && typeof input.coords === "object") ? input.coords : input;

    const lat =
      src.lat !== undefined ? src.lat :
        src.latitude !== undefined ? src.latitude :
          undefined;

    const lng =
      src.lng !== undefined ? src.lng :
        src.longitude !== undefined ? src.longitude :
          undefined;

    if (lat === undefined || lng === undefined) return null;

    const latN = Number(lat);
    const lngN = Number(lng);
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;

    return { lat: latN, lng: lngN };
  };

  // [FIX] buildPlaceMeta now reads from BOTH top-level AND placeDetails (your real payload shape)
  const buildPlaceMeta = (p) => {
    if (!p || typeof p !== "object") return null;

    const placeDetails = (p.placeDetails && typeof p.placeDetails === "object") ? p.placeDetails : null;

    const placeId = p.place_id || p.placeId || placeDetails?.place_id || null;

    const rawTypes =
      (Array.isArray(p.types) && p.types) ||
      (Array.isArray(placeDetails?.types) && placeDetails.types) ||
      null;

    const types = Array.isArray(rawTypes) ? rawTypes : null;

    const name =
      p.name ||
      placeDetails?.name ||
      null;

    const address =
      p.address ||
      p.description ||
      p.formatted_address ||
      p.label ||
      placeDetails?.formatted_address ||
      null;

    return {
      place_id: placeId,
      types,
      name,
      address,
    };
  };

  // [FIX] Robust Client-Side Airport Detection (Backup if UI missed it)
  // IMPORTANT: Do NOT mutate bookingData.instructionData here (avoid side effects).
  const pickupMeta = buildPlaceMeta(bookingData.pickup);

  const airportKeywords = [
    "airport",    // EN
    "aeroport",   // FR (accent-folded covers "aéroport")
    "flughafen",  // DE
    "aeroporto",  // IT, PT
    "aeropuerto", // ES
    "luchthaven", // NL
    "aeropot",    // Typo
    "areoport",   // Typo
  ];

  // [FIX] fold accents (aéroport -> aeroport) before matching
  const pickupText = foldText(pickupMeta?.address || pickupMeta?.name || "");

  const isAirportDetected =
    !!bookingData.instructionData?.isAirport ||
    !!bookingData.isAirport ||
    (Array.isArray(pickupMeta?.types) && pickupMeta.types.includes("airport")) ||
    airportKeywords.some((kw) => pickupText.includes(kw));

  if (isAirportDetected) {
    optionsNormalized.isAirport = true;
  }

  const returnTripNormalized = isReturnEnabled
    ? {
      enabled: true,
      scheduledAt:
        bookingData.bookingOptions?.returnScheduledAt ||
        (bookingData.returnTrip && bookingData.returnTrip.date) ||
        null
    }
    : null;

  // Build strict coords
  const originCoords = toLatLng(bookingData.pickup);
  const destCoords = toLatLng(bookingData.drop) || originCoords;

  if (!originCoords || !destCoords) {
    dispatch({
      type: FETCH_ESTIMATE_FAILED,
      payload: "Invalid pickup/drop coordinates (lat/lng missing)"
    });
    return;
  }

  // InstructionData must carry tripType/hours (backend reads instructionData.tripType)
  const instr = { ...(bookingData.instructionData || {}) };
  instr.tripType = tripTypeNormalized;

  if (isHourly) {
    instr.hours = hoursNormalized;
  }

  // propagate airport signal (backend uses pickupType OR isAirport for waiting rules)
  if (optionsNormalized.isAirport) {
    instr.isAirport = true;
    instr.pickupType = instr.pickupType || "airport";
  }

  const dropMeta = buildPlaceMeta(bookingData.drop);

  const rawWaypoints = (Array.isArray(bookingData.waypoints) && bookingData.waypoints.length > 0)
    ? bookingData.waypoints
    : (Array.isArray(bookingData.drop?.waypoints) && bookingData.drop.waypoints.length > 0)
      ? bookingData.drop.waypoints
      : null;

  // Construct Payload for /estimate
  // IMPORTANT: origin/dest must be STRICT coords only; put Places fields into originMeta/destMeta
  const payload = {
    origin: originCoords,
    dest: destCoords,

    // Optional metas for backend heuristics (airport/waiting)
    originMeta: pickupMeta,
    destMeta: dropMeta,

    carType: carTypeNormalized,

    distanceKm: bookingData.routeDetails
      ? (bookingData.routeDetails.distance_in_km || bookingData.routeDetails.distance / 1000)
      : 0,

    durationSec: bookingData.routeDetails
      ? (bookingData.routeDetails.time_in_secs || bookingData.routeDetails.duration)
      : 0,

    options: optionsNormalized,
    returnTrip: returnTripNormalized,
    instructionData: instr,
    scheduledAt: bookingData.tripDate,

    // keep for backward compatibility
    tripType: tripTypeNormalized,
    waypoints: rawWaypoints
  };

  // Add hours ONLY if hourly
  if (isHourly && hoursNormalized) {
    payload.hours = hoursNormalized;
  }

  /**console.log(
    `[ESTIMATE_PAYLOAD] tripType=${payload.tripType} carType=${payload.carType} hoursIncluded=${!!payload.hours} hours=${payload.hours || "N/A"}`
  );
  console.log("[ESTIMATE_PAYLOAD_FULL]", payload);

  console.log("[ESTIMATE_SANITY]", {
    tripTypeNormalized,
    hoursNormalized,
    origin: payload.origin,
    dest: payload.dest,
    originMeta: payload.originMeta,
    originMetaTypes: payload.originMeta?.types,
    pickupTextFolded: pickupText,
    isAirport: payload.options?.isAirport,
  });*/

  try {
    const url = `${functionsBase}/estimate`;

    /*console.log("[getEstimate] DEBUG_HOURLY bookingData:", {
      tripType: bookingData.tripType,
      bookingOptionsHours: bookingData.bookingOptions?.hours,
      optionsHours: bookingData.options?.hours
    });*/

    //console.log("[getEstimate] DEBUG_NIGHT scheduledAt:", payload.scheduledAt, "tripDate:", bookingData.tripDate);
    //console.log("[getEstimate] Calling Cloud Function:", url, payload);

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok) {
      if (response.status === 429) {
        console.warn("[getEstimate] 429 Rate Limited");
        dispatch({
          type: FETCH_ESTIMATE_FAILED,
          payload: { code: "RATE_LIMITED", message: result.message || "Too many requests" }
        });
        return { error: true, payload: { code: "RATE_LIMITED" } };
      }
      throw new Error(result.message || result.error || "Estimate Failed");
    }

    const vehicleExpiries = (result.vehicles || [])
      .map((v) => v.tokenExpiry)
      .filter(Boolean)
      .map((t) => new Date(t).getTime())
      .filter((t) => !isNaN(t) && t > 0);

    const earliestExpiry = vehicleExpiries.length > 0
      ? new Date(Math.min(...vehicleExpiries)).toISOString()
      : null;

    const polylineCoords = decodePolylinePoints(bookingData.routeDetails?.polylinePoints);
    const distanceKm = bookingData.routeDetails?.distance_in_km ?? (result.distanceKm || 0);
    const durationSec = bookingData.routeDetails?.time_in_secs ?? (result.durationSec || 0);
    const rawFare = result.price !== undefined && result.price !== null ? result.price : (result.estimateFare || 0);
    const fare = parseFloat(Number(rawFare).toFixed(2));
    const actualWaypoints = (Array.isArray(bookingData.waypoints) && bookingData.waypoints.length > 0)
      ? bookingData.waypoints
      : (Array.isArray(bookingData.drop?.waypoints) && bookingData.drop.waypoints.length > 0)
        ? bookingData.drop.waypoints
        : null;

    dispatch({
      type: FETCH_ESTIMATE_SUCCESS,
      payload: {
        ...bookingData,
        ...result,
        estimateDistance: parseFloat(Number(distanceKm).toFixed(2)),
        estimateTime: Number(durationSec),
        estimateFare: fare,
        fareCost: fare,
        coords: polylineCoords.length > 0 ? polylineCoords : (bookingData.coords || null),
        waypoints: actualWaypoints,
        pricingToken: result.pricingToken,
        tokenExpiry: result.tokenExpiry || earliestExpiry,
        timestamp: result.timestamp || Date.now(),
        pricingKey: bookingData.pricingKey
      }
    });

    return { payload: result };
  } catch (error) {
    console.error("[getEstimate] Failed:", error);
    dispatch({
      type: FETCH_ESTIMATE_FAILED,
      payload: error.message
    });
    return { error: true, payload: error };
  }
};

export const clearEstimate = () => (dispatch) => {
  dispatch({
    type: CLEAR_ESTIMATE,
    payload: null,
  });
};
